import {
  Button,
  Center,
  Heading,
  Icon,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
} from "@chakra-ui/react";
import { useMultiForm } from "@umami/components";
import { type ImplicitAccount } from "@umami/core";
import { FormProvider } from "react-hook-form";

import { LockIcon } from "../../assets/icons";
import { useColor } from "../../styles/useColor";
import { LoginButton } from "../../views/SessionLogin/LoginButton";
import { ModalBackButton } from "../BackButton";
import { ModalCloseButton } from "../CloseButton";
import { PasswordInput } from "../PasswordInput";

type MasterPasswordModalProps = {
  onSubmit: (password?: string) => Promise<void> | void;
  isLoading?: boolean;
  defaultAccount?: ImplicitAccount | null;
  submitButtonText?: string;
  title?: string;
  description?: string;
  hideCloseButtons?: boolean;
};

export const MasterPasswordModal = ({
  onSubmit,
  isLoading,
  defaultAccount,
  submitButtonText = "Submit",
  title = "Confirm password",
  description = "Enter your master password",
  hideCloseButtons = false,
}: MasterPasswordModalProps) => {
  const color = useColor();

  const form = useMultiForm<{
    password: string;
  }>({
    defaultValues: {
      password: "",
    },
  });

  const handleSubmit = async () => {
    await onSubmit();
  };

  const getPasswordField = () => {
    if (defaultAccount?.type === "social") {
      return (
        <LoginButton
          idp={defaultAccount.idp}
          isLoading={isLoading}
          onSubmit={handleSubmit}
          prefix="Confirm with"
        />
      );
    }

    return (
      <PasswordInput
        data-testid="master-password"
        inputName="password"
        label="Password"
        placeholder="Enter your password"
      />
    );
  };

  return (
    <ModalContent data-testid="master-password-modal">
      <ModalHeader>
        {!hideCloseButtons && <ModalBackButton />}
        {!hideCloseButtons && <ModalCloseButton />}
        <Center flexDirection="column" gap="12px">
          <Icon as={LockIcon} boxSize="24px" marginBottom="4px" color={color("400")} />
          <Heading size="xl">{title}</Heading>
          <Text width="full" color={color("700")} fontWeight="400" textAlign="center" size="md">
            {description}
          </Text>
        </Center>
      </ModalHeader>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(({ password }) => onSubmit(password))}>
          <ModalBody>{getPasswordField()}</ModalBody>
          {defaultAccount?.type !== "social" ? (
            <ModalFooter>
              <Button width="full" isLoading={isLoading} type="submit" variant="primary">
                {submitButtonText}
              </Button>
            </ModalFooter>
          ) : null}
        </form>
      </FormProvider>
    </ModalContent>
  );
};
